import {
  selectAllFormTemplates,
  selectAllFormSubmissions
} from "../../../../../store/reducers/formTemplates/FormTemplatesSlice";
import { useSelector } from "react-redux";
import { useState, useEffect, useMemo } from "react";
import PageTitle from "../../../../../utils/PageTitle";
import StepperComponent from "../OnboardingStepper";
import SideMenuLayout from "../SideMenuLayout";
import TermsSheet from "./TermsSheet";
import { useHistory } from "react-router-dom";
import { PATHS } from "constants/pathConstants";
import { FormTemplate, FormSubmission } from "apis/v3/formTemplatesApi";

const TermsPage = () => {
  PageTitle("Terms of Service");
  const history = useHistory();

  const allFormTemplates = useSelector(selectAllFormTemplates);
  const allFormSubmissions = useSelector(selectAllFormSubmissions);

  const submissionIds = useMemo(() => {
    return new Set(
      allFormSubmissions.map(
        (submission: FormSubmission) => submission.formInfo.id
      )
    );
  }, [allFormSubmissions]);

  const formsNeedingAcceptance = useMemo(() => {
    return allFormTemplates.filter(
      (template: FormTemplate) => !submissionIds.has(template.id)
    );
  }, [allFormTemplates, submissionIds]);

  const [initialFormsToComplete] = useState<FormTemplate[]>(
    () => formsNeedingAcceptance
  );

  const baseStepIndex = 5;

  const legalFormSubsteps = useMemo(() => {
    const decorated = allFormTemplates.map(
      (form: FormTemplate, index: number) => ({
        label: form.title,
        stepIndex: baseStepIndex + index,
        isComplete: submissionIds.has(form.id),
        id: form.id
      })
    );
    const completed = decorated.filter((step) => step.isComplete);
    const pending = decorated.filter((step) => !step.isComplete);
    return [...completed, ...pending];
  }, [allFormTemplates, baseStepIndex, submissionIds]);

  const allAgreementsCurrent =
    formsNeedingAcceptance.length === 0 || initialFormsToComplete.length === 0;
  const [currentFormIndex, setCurrentFormIndex] = useState(0);

  // Skip if no agreements need accepting
  useEffect(() => {
    if (allAgreementsCurrent) {
      history.push(PATHS.ONBOARDING_PROFILE);
    }
  }, [allAgreementsCurrent, history]);

  const handleClickContinue = () => {
    if (currentFormIndex < initialFormsToComplete.length - 1) {
      setCurrentFormIndex(currentFormIndex + 1);
    } else {
      history.push(PATHS.ONBOARDING_PROFILE);
    }
  };

  const currentForm = initialFormsToComplete[currentFormIndex];
  const sortedSubsteps = legalFormSubsteps;
  const currentFormGlobalIndex = sortedSubsteps.findIndex(
    (step) => step.id === currentForm?.id
  );
  const currentStepIndex =
    currentFormGlobalIndex >= 0
      ? sortedSubsteps[currentFormGlobalIndex].stepIndex
      : baseStepIndex;

  if (!currentForm) {
    return null;
  }

  return (
    <SideMenuLayout
      onboardingStepper={
        <StepperComponent
          activeStepIndex={currentStepIndex}
          legalFormSubsteps={legalFormSubsteps}
        />
      }
    >
      <TermsSheet
        onClickContinue={handleClickContinue}
        formId={currentForm.id}
        formTitle={currentForm.title}
      />
    </SideMenuLayout>
  );
};

export default TermsPage;
